(() => {
  const silvraDebounce = (callback, wait) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const escapeHTML = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const formatSearchPrice = (value) => {
    const amount = Number.parseFloat(value);
    const currency = window.Shopify?.currency?.active;
    if (!Number.isFinite(amount) || !currency) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch (_error) {
      return '';
    }
  };

  if (!customElements.get('search-form') && typeof globalThis.SearchForm === 'undefined') {
    globalThis.SearchForm = class SearchForm extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input[type="search"]');
        this.resetButton = this.querySelector('button[type="reset"]');

        if (this.input?.form) {
          this.input.form.addEventListener('reset', this.onFormReset.bind(this));
          this.input.addEventListener(
            'input',
            silvraDebounce(() => this.onChange(), 300)
          );
        }
      }

      toggleResetButton() {
        if (!this.input || !this.resetButton) return;
        this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
      }

      onChange() {
        this.toggleResetButton();
      }

      shouldResetForm() {
        return !document.querySelector('[aria-selected="true"] a');
      }

      onFormReset(event) {
        event.preventDefault();
        if (!this.shouldResetForm() || !this.input) return;
        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
      }
    };
  }

  class SilvraHeader extends HTMLElement {
    constructor() {
      super();
      this.menuCloseTimer = null;
      this.searchCloseTimer = null;
      this.megaCloseTimer = null;
      this.searchStates = new WeakMap();
      this.searchCache = new Map();
      this.activeSurface = null;
      this.lastSurfaceTrigger = null;
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onStickyPositionChange = this.onStickyPositionChange.bind(this);
      this.onMobileViewportResize = this.onMobileViewportResize.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onMenuDrawerClick = this.onMenuDrawerClick.bind(this);
      this.onMenuOverlayClick = this.onMenuOverlayClick.bind(this);
    }

    connectedCallback() {
      this.sectionHost = this.closest('.silvra-header-section-host');
      if (this.classList.contains('silvra-header--sticky')) {
        this.sectionHost?.classList.add('silvra-header-section--sticky');
      }

      this.menuDrawer = this.querySelector('[data-silvra-menu-drawer]');
      this.menuOverlay = this.querySelector('[data-silvra-menu-overlay]');
      this.menuTrigger = this.querySelector('[data-silvra-action="menu-open"]');
      this.mobileSearch = this.querySelector('[data-silvra-mobile-search]');
      this.searchOverlay = this.querySelector('[data-silvra-search-overlay]');
      this.searchTrigger = this.querySelector('[data-silvra-action="search-open"]');
      this.mobileMedia = window.matchMedia('(min-width: 990px)');
      this.stickyAnchor = this.previousElementSibling?.matches('[data-silvra-sticky-anchor]')
        ? this.previousElementSibling
        : null;

      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        this.initializeSearchInstance(instance);
      });

      this.mountMobileMenuPortal();
      this.menuDrawer?.addEventListener('click', this.onMenuDrawerClick);
      this.menuDrawer?.addEventListener('keydown', this.onKeyDown);
      this.menuOverlay?.addEventListener('click', this.onMenuOverlayClick);

      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      this.mobileMedia.addEventListener?.('change', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onMobileViewportResize);

      if (this.classList.contains('silvra-header--sticky')) {
        window.addEventListener('scroll', this.onStickyPositionChange, { passive: true });
        window.addEventListener('resize', this.onStickyPositionChange);
        this.updateStickyPosition();
      }

      this.bindMegaMenus();
      this.ensureCartIntegration();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);
      this.menuDrawer?.removeEventListener('click', this.onMenuDrawerClick);
      this.menuDrawer?.removeEventListener('keydown', this.onKeyDown);
      this.menuOverlay?.removeEventListener('click', this.onMenuOverlayClick);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      this.mobileMedia?.removeEventListener?.('change', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onMobileViewportResize);
      window.removeEventListener('scroll', this.onStickyPositionChange);
      window.removeEventListener('resize', this.onStickyPositionChange);
      if (this.stickyAnimationFrame) window.cancelAnimationFrame(this.stickyAnimationFrame);
      this.classList.remove('silvra-header--mobile-fixed', 'silvra-header--desktop-fixed');
      this.sectionHost?.classList.remove(
        'silvra-header-section--sticky',
        'silvra-header-section--mobile-fixed',
        'silvra-header-section--desktop-fixed',
        'silvra-header-section--search-open'
      );
      this.classList.remove('silvra-header--search-results-open');
      this.classList.remove('silvra-header--mega-open');
      this.sectionHost?.classList.remove('silvra-header-section--menu-open');
      this.sectionHost?.style.removeProperty('--silvra-sticky-section-height');
      this.sectionHost?.style.removeProperty('--silvra-sticky-top-offset');
      document.body.classList.remove('silvra-header-is-locked');

      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        const state = this.searchStates.get(instance);
        window.clearTimeout(state?.timer);
        state?.controller?.abort();
      });

      this.mobileMenuPortal?.remove();
      this.mobileMenuPortal = null;
    }

    mountMobileMenuPortal() {
      if (!this.menuDrawer || !this.menuOverlay || this.mobileMenuPortal) return;

      const portal = document.createElement('div');
      const colorClasses = Array.from(this.classList).filter((className) => className.startsWith('color-'));
      portal.className = ['silvra-header', 'silvra-header__mobile-portal', ...colorClasses].join(' ');
      portal.setAttribute('style', this.getAttribute('style') || '');
      portal.setAttribute('data-silvra-mobile-portal', '');

      portal.append(this.menuOverlay, this.menuDrawer);
      document.body.appendChild(portal);
      this.mobileMenuPortal = portal;
    }

    onMenuDrawerClick(event) {
      const closeButton = event.target.closest('[data-silvra-action="menu-close"]');
      const panelOpen = event.target.closest('[data-silvra-panel-open]');
      const panelBack = event.target.closest('[data-silvra-panel-back]');

      if (closeButton) {
        this.closeMenu();
        return;
      }

      if (panelOpen) {
        this.showMobilePanel(panelOpen.dataset.silvraPanelOpen);
        return;
      }

      if (panelBack) this.showMobilePanel(panelBack.dataset.silvraPanelBack);
    }

    onMenuOverlayClick() {
      this.closeMenu();
    }

    ensureCartIntegration() {
      const cartType = this.dataset.cartType;
      const legacyHeaderIsPresent = Boolean(document.querySelector('.header-wrapper'));

      if (legacyHeaderIsPresent) return;

      if (cartType === 'drawer' && !customElements.get('cart-drawer')) {
        this.loadThemeScript(this.dataset.cartDrawerScript, 'cart-drawer');
      }

      if (cartType === 'notification' && !customElements.get('cart-notification')) {
        this.loadThemeScript(this.dataset.cartNotificationScript, 'cart-notification');
      }
    }

    loadThemeScript(source, key) {
      if (!source || document.querySelector(`script[data-silvra-dependency="${key}"]`)) return;
      const script = document.createElement('script');
      script.src = source;
      script.defer = true;
      script.dataset.silvraDependency = key;
      document.head.appendChild(script);
    }

    bindMegaMenus() {
      const navShell = this.querySelector('.silvra-header__nav-shell');
      if (!navShell) return;

      navShell.querySelectorAll('.silvra-header__nav-item').forEach((item) => {
        item.addEventListener('pointerenter', () => {
          this.cancelMegaClose();
          if (item.matches('[data-silvra-mega-item]')) this.openMega(item);
          else this.closeAllMegaMenus();
        });
      });

      navShell.addEventListener('pointerenter', () => this.cancelMegaClose());
      navShell.addEventListener('pointerleave', () => this.scheduleMegaClose());
    }

    openMega(item) {
      if (window.matchMedia('(max-width: 989px)').matches) return;
      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        this.closeSearchResults(instance);
      });

      const currentItem = this.querySelector('[data-silvra-mega-item].is-open');
      if (currentItem === item) return;

      const isSwitching = Boolean(currentItem);
      if (isSwitching) {
        currentItem.classList.add('is-switching');
        item.classList.add('is-switching');
      }

      item.classList.remove('is-escape-closed');
      item.classList.add('is-open');
      item.querySelector('[data-silvra-mega-trigger]')?.setAttribute('aria-expanded', 'true');
      this.classList.add('silvra-header--mega-open');
      this.sectionHost?.classList.add('silvra-header-section--menu-open');
      this.closeAllMegaMenus(item);

      if (isSwitching) {
        item.querySelector('.silvra-header__mega-menu')?.offsetHeight;
        currentItem.classList.remove('is-switching');
        item.classList.remove('is-switching');
      }
    }

    closeMega(item, escapeClosed = false) {
      if (!item) return;
      item.classList.remove('is-open');
      item.classList.toggle('is-escape-closed', escapeClosed);
      item.querySelector('[data-silvra-mega-trigger]')?.setAttribute('aria-expanded', 'false');

      if (!this.querySelector('[data-silvra-mega-item].is-open')) {
        this.classList.remove('silvra-header--mega-open');
        this.sectionHost?.classList.remove('silvra-header-section--menu-open');
      }
    }

    closeAllMegaMenus(except = null) {
      this.querySelectorAll('[data-silvra-mega-item]').forEach((item) => {
        if (item !== except) this.closeMega(item);
      });
    }

    scheduleMegaClose() {
      const navShell = this.querySelector('.silvra-header__nav-shell');
      if (!navShell) return;

      this.cancelMegaClose();
      this.megaCloseTimer = window.setTimeout(() => {
        if (!navShell.matches(':hover') && !navShell.contains(document.activeElement)) this.closeAllMegaMenus();
        this.megaCloseTimer = null;
      }, 120);
    }

    cancelMegaClose() {
      if (!this.megaCloseTimer) return;
      window.clearTimeout(this.megaCloseTimer);
      this.megaCloseTimer = null;
    }

    onClick(event) {
      const actionElement = event.target.closest('[data-silvra-action]');
      const panelOpen = event.target.closest('[data-silvra-panel-open]');
      const panelBack = event.target.closest('[data-silvra-panel-back]');
      const desktopMenuLink = event.target.closest('[data-silvra-mega-trigger], [data-silvra-mega-menu] a');
      const searchReset = event.target.closest('.silvra-header__search-reset');
      const searchInput = event.target.closest('.silvra-header__search-input');

      if (searchInput && this.contains(searchInput)) {
        const instance = searchInput.closest('[data-silvra-search-instance]');
        const state = this.searchStates.get(instance);
        if (state?.results.hidden) {
          if (searchInput.value.trim()) this.schedulePredictiveSearch(searchInput, 0);
          else this.showDefaultSearchSuggestions(instance);
        }
      }

      if (searchReset && this.contains(searchReset)) {
        event.preventDefault();
        const instance = searchReset.closest('[data-silvra-search-instance]');
        const input = instance?.querySelector('.silvra-header__search-input');
        if (input) input.value = '';
        searchReset.classList.add('hidden');
        this.showDefaultSearchSuggestions(instance);
        input?.focus();
        return;
      }

      if (desktopMenuLink && this.contains(desktopMenuLink)) this.closeAllMegaMenus();

      if (actionElement && this.contains(actionElement)) {
        const action = actionElement.dataset.silvraAction;
        if (action === 'menu-open') this.openMenu(actionElement);
        if (action === 'menu-close') this.closeMenu();
        if (action === 'search-open') this.openSearch(actionElement);
        if (action === 'search-close') this.closeSearch();
      }

      if (panelOpen && this.contains(panelOpen)) {
        this.showMobilePanel(panelOpen.dataset.silvraPanelOpen);
      }

      if (panelBack && this.contains(panelBack)) {
        this.showMobilePanel(panelBack.dataset.silvraPanelBack);
      }
    }

    onInput(event) {
      if (!event.target.matches('.silvra-header__search-input')) return;
      const form = event.target.closest('.silvra-header__search-form');
      form?.querySelector('.silvra-header__search-reset')?.classList.toggle('hidden', !event.target.value);
      const instance = event.target.closest('[data-silvra-search-instance]');
      const state = this.searchStates.get(instance);
      this.schedulePredictiveSearch(event.target);
    }

    onSubmit(event) {
      const form = event.target.closest('.silvra-header__search-form');
      if (!form || !this.contains(form)) return;
      const instance = form.closest('[data-silvra-search-instance]');
      const state = this.searchStates.get(instance);
      const query = state?.input.value.trim();
      if (!state || !query) return;

      event.preventDefault();
      window.location.assign(this.getSearchUrl(query, true));
    }

    onChange(event) {
      if (!event.target.matches('.silvra-header__localization-select')) return;
      const form = event.target.closest('form');
      if (form?.requestSubmit) form.requestSubmit();
      else form?.submit();
    }

    onFocusIn(event) {
      const searchInstance = event.target.closest('[data-silvra-search-instance]');
      if (searchInstance) {
        this.clearOtherSearchInstances(searchInstance);
        const input = searchInstance.querySelector('.silvra-header__search-input');
        if (event.target === input) {
          if (input.value.trim()) this.schedulePredictiveSearch(input, 0);
          else this.showDefaultSearchSuggestions(searchInstance);
        }
      }

      const megaItem = event.target.closest('[data-silvra-mega-item]');
      if (megaItem && this.contains(megaItem)) {
        this.cancelMegaClose(megaItem);
        this.openMega(megaItem);
      }
    }

    onFocusOut(event) {
      const searchInstance = event.target.closest('[data-silvra-search-instance]');
      if (searchInstance) {
        window.setTimeout(() => {
          if (!searchInstance.contains(document.activeElement)) this.closeSearchResults(searchInstance);
        }, 0);
      }

      const megaItem = event.target.closest('[data-silvra-mega-item]');
      if (!megaItem || !this.contains(megaItem)) return;
      window.setTimeout(() => {
        if (!megaItem.contains(document.activeElement) && !megaItem.matches(':hover')) this.closeMega(megaItem);
      }, 0);
    }

    onKeyDown(event) {
      const searchInstance = event.target.closest('[data-silvra-search-instance]');
      if (searchInstance && this.handleSearchKeyboard(event, searchInstance)) return;

      if (event.key === 'Escape') {
        if (this.mobileSearch?.classList.contains('is-open')) {
          event.preventDefault();
          this.closeSearch();
          return;
        }

        if (this.menuDrawer?.classList.contains('is-open')) {
          event.preventDefault();
          this.closeMenu();
          return;
        }

        const megaItem = event.target.closest('[data-silvra-mega-item]');
        if (megaItem) {
          event.preventDefault();
          this.closeMega(megaItem, true);
          megaItem.querySelector('[data-silvra-mega-trigger]')?.focus();
          return;
        }
      }

      if (event.key === 'Tab' && this.activeSurface) this.trapSurfaceFocus(event, this.activeSurface);
    }

    onDocumentPointerDown(event) {
      if (this.contains(event.target)) return;
      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        this.closeSearchResults(instance);
      });
    }

    onViewportChange(event) {
      this.closeMenu(false);
      this.closeSearch(false);
      this.closeAllMegaMenus();
      this.updateStickyPosition();
    }

    onStickyPositionChange() {
      if (this.stickyAnimationFrame) return;
      this.stickyAnimationFrame = window.requestAnimationFrame(() => {
        this.stickyAnimationFrame = null;
        this.updateStickyPosition();
      });
    }

    updateStickyPosition() {
      if (!this.classList.contains('silvra-header--sticky') || !this.stickyAnchor) {
        this.classList.remove('silvra-header--mobile-fixed', 'silvra-header--desktop-fixed');
        this.sectionHost?.classList.remove(
          'silvra-header-section--mobile-fixed',
          'silvra-header-section--desktop-fixed'
        );
        this.sectionHost?.style.removeProperty('--silvra-sticky-section-height');
        this.sectionHost?.style.removeProperty('--silvra-sticky-top-offset');
        return;
      }

      const stickyTopOffset = this.getStickyTopOffset();
      this.sectionHost?.style.setProperty('--silvra-sticky-top-offset', `${stickyTopOffset}px`);
      const shouldFix = window.scrollY > 0 && this.stickyAnchor.getBoundingClientRect().top <= stickyTopOffset;
      const isDesktop = Boolean(this.mobileMedia?.matches);

      if (isDesktop) {
        this.sectionHost?.style.setProperty('--silvra-sticky-section-height', `${this.offsetHeight}px`);
      }

      this.classList.toggle('silvra-header--desktop-fixed', shouldFix && isDesktop);
      this.classList.toggle('silvra-header--mobile-fixed', shouldFix && !isDesktop);
      this.sectionHost?.classList.toggle('silvra-header-section--desktop-fixed', shouldFix && isDesktop);
      this.sectionHost?.classList.toggle('silvra-header-section--mobile-fixed', shouldFix && !isDesktop);

      if (!isDesktop) this.sectionHost?.style.removeProperty('--silvra-sticky-section-height');
    }

    getStickyTopOffset() {
      let offset = 0;
      document
        .querySelectorAll('.announcement-bar-section, [class^="section-"][class*=" announcement-"]')
        .forEach((element) => {
          if (element.closest('.silvra-header-section-host')) return;
          const style = window.getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden') return;
          const rect = element.getBoundingClientRect();
          if (rect.top <= 1 && rect.bottom > 0 && rect.height <= 150) {
            offset = Math.max(offset, Math.min(rect.bottom, rect.height));
          }
        });
      return Math.max(0, Math.round(offset));
    }

    updateMobileMenuHeight() {
      if (!this.menuDrawer?.classList.contains('is-open')) return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const availableHeight = Math.max(0, Math.round(viewportHeight));
      this.style.setProperty('--silvra-mobile-menu-height', `${availableHeight}px`);
    }

    onMobileViewportResize() {
      this.updateMobileMenuHeight();
    }

    openMenu(trigger) {
      if (!this.menuDrawer || !this.menuOverlay) return;
      this.closeSearch(false);
      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        this.closeSearchResults(instance);
      });
      if (this.contains(document.activeElement) && document.activeElement.matches('.silvra-header__search-input')) {
        document.activeElement.blur();
      }
      this.resetMobilePanels();
      this.lastSurfaceTrigger = trigger;
      this.menuDrawer.hidden = false;
      this.menuOverlay.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('silvra-header-is-locked');
      this.activeSurface = this.menuDrawer;

      window.requestAnimationFrame(() => {
        this.menuDrawer.classList.add('is-open');
        this.menuOverlay.classList.add('is-open');
        this.updateMobileMenuHeight();
        this.menuDrawer.querySelector('[data-silvra-action="menu-close"]')?.focus();
      });
    }

    closeMenu(restoreFocus = true) {
      if (!this.menuDrawer || !this.menuOverlay) return;
      this.menuDrawer.classList.remove('is-open');
      this.menuOverlay.classList.remove('is-open');
      this.menuTrigger?.setAttribute('aria-expanded', 'false');
      if (this.activeSurface === this.menuDrawer) this.activeSurface = null;
      this.unlockBodyIfPossible();

      window.clearTimeout(this.menuCloseTimer);
      this.menuCloseTimer = window.setTimeout(() => {
        if (!this.menuDrawer.classList.contains('is-open')) {
          this.menuDrawer.hidden = true;
          this.menuOverlay.hidden = true;
          this.style.removeProperty('--silvra-mobile-menu-height');
        }
      }, 210);

      if (restoreFocus) (this.lastSurfaceTrigger || this.menuTrigger)?.focus();
    }

    openSearch(trigger) {
      if (!this.mobileSearch || !this.searchOverlay) return;
      this.closeMenu(false);
      this.lastSurfaceTrigger = trigger;
      this.mobileSearch.hidden = false;
      this.searchOverlay.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('silvra-header-is-locked');
      this.activeSurface = this.mobileSearch;

      const mobileInstance = this.mobileSearch.querySelector('[data-silvra-search-instance]');
      if (mobileInstance) this.clearOtherSearchInstances(mobileInstance);

      window.requestAnimationFrame(() => {
        this.mobileSearch.classList.add('is-open');
        this.searchOverlay.classList.add('is-open');
        window.setTimeout(() => this.mobileSearch.querySelector('input[type="search"]')?.focus(), 40);
      });
    }

    closeSearch(restoreFocus = true) {
      if (!this.mobileSearch || !this.searchOverlay) return;
      this.mobileSearch.classList.remove('is-open');
      this.searchOverlay.classList.remove('is-open');
      this.searchTrigger?.setAttribute('aria-expanded', 'false');
      if (this.activeSurface === this.mobileSearch) this.activeSurface = null;
      this.clearSearchInstance(this.mobileSearch.querySelector('[data-silvra-search-instance]'));
      this.unlockBodyIfPossible();

      window.clearTimeout(this.searchCloseTimer);
      this.searchCloseTimer = window.setTimeout(() => {
        if (!this.mobileSearch.classList.contains('is-open')) {
          this.mobileSearch.hidden = true;
          this.searchOverlay.hidden = true;
        }
      }, 190);

      if (restoreFocus) (this.lastSurfaceTrigger || this.searchTrigger)?.focus();
    }

    unlockBodyIfPossible() {
      if (!this.menuDrawer?.classList.contains('is-open') && !this.mobileSearch?.classList.contains('is-open')) {
        document.body.classList.remove('silvra-header-is-locked');
      }
    }

    async showMobilePanel(panelId) {
      if (!panelId || !this.menuDrawer || this.mobilePanelTransitioning) return;
      const target = this.menuDrawer.querySelector(`#${CSS.escape(panelId)}`);
      const active = this.menuDrawer.querySelector('[data-silvra-mobile-panel].is-active');
      if (!target || target === active) return;

      const isBack = active?.dataset.silvraPanelParent === panelId;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reducedMotion ? 0 : 260;
      const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
      const targetStart = isBack ? 'translateX(-24%)' : 'translateX(100%)';
      const activeEnd = isBack ? 'translateX(100%)' : 'translateX(-24%)';

      this.mobilePanelTransitioning = true;
      target.hidden = false;
      target.scrollTop = 0;
      target.classList.add('is-active');

      if (!active || duration === 0 || typeof target.animate !== 'function') {
        if (active) {
          active.classList.remove('is-active');
          active.hidden = true;
        }
        this.mobilePanelTransitioning = false;
        target.querySelector('[data-silvra-panel-back], .silvra-header__mobile-link')?.focus();
        return;
      }

      const activeAnimation = active.animate(
        [{ transform: 'translateX(0)' }, { transform: activeEnd }],
        { duration, easing, fill: 'both' }
      );
      const targetAnimation = target.animate(
        [{ transform: targetStart }, { transform: 'translateX(0)' }],
        { duration, easing, fill: 'both' }
      );

      try {
        await Promise.all([activeAnimation.finished, targetAnimation.finished]);
      } catch (_error) {
        // Animationen kan avbrytas när menyn stängs eller förhandsvisningen laddas om.
      }

      active.classList.remove('is-active');
      active.hidden = true;
      activeAnimation.cancel();
      targetAnimation.cancel();
      this.mobilePanelTransitioning = false;
      target.querySelector('[data-silvra-panel-back], .silvra-header__mobile-link')?.focus();
    }

    resetMobilePanels() {
      if (!this.menuDrawer) return;
      this.mobilePanelTransitioning = false;
      const root = this.menuDrawer.querySelector('[data-silvra-mobile-panel]:not([data-silvra-panel-parent])');
      this.menuDrawer.querySelectorAll('[data-silvra-mobile-panel]').forEach((panel) => {
        panel.getAnimations?.().forEach((animation) => animation.cancel());
        const isRoot = panel === root;
        panel.hidden = !isRoot;
        panel.classList.toggle('is-active', isRoot);
      });
      if (root) root.scrollTop = 0;
    }

    initializeSearchInstance(instance) {
      const input = instance.querySelector('.silvra-header__search-input');
      const results = instance.querySelector('[data-predictive-search]');
      if (!input || !results) return;

      this.searchStates.set(instance, {
        input,
        results,
        status: instance.querySelector('[data-silvra-search-status]'),
        defaultMarkup: results.innerHTML,
        controller: null,
        timer: null,
        query: '',
      });
    }

    showDefaultSearchSuggestions(instance) {
      const state = this.searchStates.get(instance);
      if (!state) return;
      window.clearTimeout(state.timer);
      state.controller?.abort();
      state.controller = null;
      state.query = '';
      state.results.innerHTML = state.defaultMarkup;
      state.status.textContent = 'Populära sökningar visas.';

      if (state.results.querySelector('a')) this.openSearchResults(instance);
      else this.closeSearchResults(instance);
    }

    schedulePredictiveSearch(input, delay = 180) {
      const instance = input.closest('[data-silvra-search-instance]');
      const state = this.searchStates.get(instance);
      if (!state) return;

      window.clearTimeout(state.timer);
      const query = input.value.trim();
      if (!query) {
        this.showDefaultSearchSuggestions(instance);
        return;
      }

      state.timer = window.setTimeout(() => this.getPredictiveSearch(instance, query), delay);
    }

    async getPredictiveSearch(instance, query) {
      const state = this.searchStates.get(instance);
      if (!state || state.input.value.trim() !== query) return;

      const cacheKey = query.toLocaleLowerCase(document.documentElement.lang || 'sv');
      if (this.searchCache.has(cacheKey)) {
        const cachedResults = this.searchCache.get(cacheKey);
        state.query = query;
        this.renderPredictiveSearch(instance, query, cachedResults);
        return cachedResults;
      }

      state.controller?.abort();
      state.controller = new AbortController();
      state.query = query;
      state.results.innerHTML = `
        <div class="silvra-header__search-loading" role="presentation">
          <span class="silvra-header__search-spinner" aria-hidden="true"></span>
          <span>Söker…</span>
        </div>`;
      state.status.textContent = 'Söker efter matchande produkter och kategorier.';
      this.openSearchResults(instance);

      try {
        const exactResults = await this.fetchPredictiveResults(query, state.controller.signal);
        const prefixQuery = this.getPrefixSearchQuery(query);
        const prefixResults =
          prefixQuery !== query
            ? await this.fetchPredictiveResults(prefixQuery, state.controller.signal)
            : null;

        let searchResults = this.mergePredictiveResults(exactResults, prefixResults, [], query);
        const initialResultCount =
          searchResults.products.length +
          (searchResults.queries?.length || 0) +
          (searchResults.collections?.length || 0);

        if (initialResultCount && state.input.value.trim() === query) {
          this.renderPredictiveSearch(instance, query, searchResults);
        }

        if (query.length >= 3 && searchResults.products.length < 6) {
          try {
            const fallbackProducts = await this.fetchSearchPageProducts(query, state.controller.signal);
            searchResults = this.mergePredictiveResults(
              exactResults,
              prefixResults,
              fallbackProducts,
              query
            );
          } catch (fallbackError) {
            if (fallbackError.name === 'AbortError') throw fallbackError;
          }
        }

        this.searchCache.set(cacheKey, searchResults);

        if (state.input.value.trim() === query) this.renderPredictiveSearch(instance, query, searchResults);
        return searchResults;
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (state.input.value.trim() !== query) return;

        state.results.innerHTML = `
          <div class="silvra-header__search-empty">
            <p>Sökförslagen kunde inte laddas just nu.</p>
            <a class="silvra-header__search-all" href="${escapeHTML(this.getSearchUrl(query, true))}">Visa sökresultat för ”${escapeHTML(query)}”</a>
          </div>`;
        state.status.textContent = 'Sökförslagen kunde inte laddas. Du kan fortfarande visa alla sökresultat.';
        this.openSearchResults(instance);
        return null;
      }
    }

    async fetchPredictiveResults(query, signal) {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
      const url = new URL(`${normalizedRoot}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'query,product,collection');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,tag,variants.sku'
      );
      url.searchParams.set('resources[options][unavailable_products]', 'hide');

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

      const payload = await response.json();
      return payload?.resources?.results || {};
    }

    async fetchSearchPageProducts(query, signal) {
      const response = await fetch(this.getSearchUrl(query, true), {
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) throw new Error(`Search fallback failed with ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(documentFragment.querySelectorAll('#product-grid .card-wrapper'))
        .slice(0, 24)
        .map((card) => {
          const link = card.querySelector('.card-heading a[href]');
          const image = card.querySelector('.card-media img');
          const imageSource = image?.getAttribute('src') || '';
          const regularPrice = card.querySelector('.price__regular .price-item--regular');
          const salePrice = card.querySelector('.price__sale .price-item--sale');
          const priceText = (salePrice?.textContent || regularPrice?.textContent || '').trim();
          const title = link?.textContent?.trim() || image?.getAttribute('alt')?.trim() || '';

          if (!link || !title) return null;
          return {
            id: card.dataset.cardProductId || link.getAttribute('href'),
            title,
            url: link.getAttribute('href'),
            price_text: priceText,
            featured_image: imageSource
              ? {
                  url: new URL(imageSource, window.location.origin).href,
                  alt: image?.getAttribute('alt') || title,
                }
              : null,
          };
        })
        .filter(Boolean);
    }

    mergePredictiveResults(exactResults = {}, prefixResults = null, fallbackProducts = [], query = '') {
      const exactProducts = Array.isArray(exactResults.products) ? exactResults.products : [];
      const prefixProducts = Array.isArray(prefixResults?.products) ? prefixResults.products : [];
      const seenProducts = new Set();
      const rankedProducts = [...exactProducts, ...prefixProducts, ...fallbackProducts]
        .filter((product) => {
          const productPath = product.url
            ? new URL(product.url, window.location.origin).pathname
            : '';
          const key =
            productPath ||
            (product.handle ? `/products/${product.handle}` : '') ||
            this.normalizeSearchText(product.title || '');
          if (!key || seenProducts.has(key)) return false;
          seenProducts.add(key);
          return true;
        })
        .map((product, index) => ({
          product,
          index,
          score: this.getProductQueryScore(product, query),
        }))
        .sort((a, b) => b.score - a.score || a.index - b.index);
      const relevantProducts = rankedProducts.filter(({ score }) => score > 0);
      const products = (relevantProducts.length ? relevantProducts : rankedProducts)
        .slice(0, 6)
        .map(({ product }) => product);

      return {
        ...exactResults,
        products,
      };
    }

    normalizeSearchText(value = '') {
      return String(value)
        .toLocaleLowerCase(document.documentElement.lang || 'sv')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    getProductQueryScore(product, query) {
      const queryTerms = this.normalizeSearchText(query).split(/\s+/).filter(Boolean);
      if (!queryTerms.length) return 0;

      const title = this.normalizeSearchText(product.title || '');
      const titleWords = title.split(/\s+/).filter(Boolean);
      const secondaryWords = this.normalizeSearchText(
        [product.vendor, product.product_type, product.type].filter(Boolean).join(' ')
      )
        .split(/\s+/)
        .filter(Boolean);
      let score = 0;

      queryTerms.forEach((term) => {
        if (titleWords.includes(term)) score += 100;
        else if (titleWords.some((word) => word.startsWith(term))) score += 80;
        else if (term.length >= 3 && titleWords.some((word) => word.includes(term))) score += 35;
        else if (secondaryWords.includes(term)) score += 55;
        else if (secondaryWords.some((word) => word.startsWith(term))) score += 40;
        else score -= 60;
      });

      const normalizedQuery = queryTerms.join(' ');
      if (title === normalizedQuery) score += 180;
      else if (title.startsWith(normalizedQuery)) score += 90;

      return score;
    }

    renderPredictiveSearch(instance, query, searchResults) {
      const state = this.searchStates.get(instance);
      if (!state) return;

      const queries = Array.isArray(searchResults.queries) ? searchResults.queries : [];
      const collections = Array.isArray(searchResults.collections) ? searchResults.collections : [];
      const products = Array.isArray(searchResults.products) ? searchResults.products : [];
      const textResults = [...queries, ...collections].slice(0, 7);
      const totalResults = textResults.length + products.length;
      let markup = '<div class="silvra-header__search-results-content">';

      state.query = query;

      if (products.length) {
        markup += '<section class="silvra-header__search-result-group"><p class="silvra-header__search-results-label">Produkter</p><ul class="silvra-header__search-product-list" role="list">';
        products.forEach((product) => {
          const imageObject = product.featured_image;
          const imageUrl = imageObject?.url || imageObject || product.image || '';
          const imageAlt = imageObject?.alt || product.title || '';
          const price = product.price_text || formatSearchPrice(product.price || product.price_min);
          markup += `
            <li>
              <a class="silvra-header__search-product" role="option" href="${escapeHTML(product.url || this.getSearchUrl(product.title || query))}">
                <span class="silvra-header__search-product-image-wrap">
                  ${imageUrl ? `<img class="silvra-header__search-product-image" src="${escapeHTML(imageUrl)}" alt="${escapeHTML(imageAlt)}" width="72" height="72" loading="lazy">` : ''}
                </span>
                <span class="silvra-header__search-product-copy">
                  ${product.vendor ? `<span class="silvra-header__search-product-vendor">${escapeHTML(product.vendor)}</span>` : ''}
                  <span class="silvra-header__search-product-title">${escapeHTML(product.title || '')}</span>
                  ${price ? `<span class="silvra-header__search-product-price">${escapeHTML(price)}</span>` : ''}
                </span>
              </a>
            </li>`;
        });
        markup += '</ul></section>';
      }

      if (textResults.length) {
        markup += '<section class="silvra-header__search-result-group"><p class="silvra-header__search-results-label">Förslag</p><ul class="silvra-header__search-suggestion-list" role="list">';
        textResults.forEach((item) => {
          const title = item.text || item.title || item.query || '';
          const href = item.url || this.getSearchUrl(title);
          if (!title) return;
          markup += `<li><a class="silvra-header__search-suggestion-link" role="option" href="${escapeHTML(href)}"><span>${escapeHTML(title)}</span><span aria-hidden="true">→</span></a></li>`;
        });
        markup += '</ul></section>';
      }

      if (!totalResults) {
        markup += `<div class="silvra-header__search-empty"><p>Inga direkta träffar för ”${escapeHTML(query)}”.</p></div>`;
      }

      markup += `<a class="silvra-header__search-all" href="${escapeHTML(this.getSearchUrl(query, true))}">Visa alla resultat för ”${escapeHTML(query)}” <span aria-hidden="true">→</span></a></div>`;
      state.results.innerHTML = markup;
      state.status.textContent = totalResults
        ? `${totalResults} sökförslag hittades.`
        : `Inga direkta sökförslag hittades för ${query}.`;
      this.openSearchResults(instance);
    }

    getSearchUrl(query, prefixLastWord = false) {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
      const url = new URL(`${normalizedRoot}search`, window.location.origin);
      url.searchParams.set('q', prefixLastWord ? this.getPrefixSearchQuery(query) : query);
      url.searchParams.set('options[prefix]', 'last');
      return `${url.pathname}${url.search}`;
    }

    getPrefixSearchQuery(query) {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || /["*:]/.test(trimmedQuery)) return trimmedQuery;

      const words = trimmedQuery.split(/\s+/);
      const lastWord = words[words.length - 1];
      if (!lastWord || lastWord.length < 2) return trimmedQuery;
      words[words.length - 1] = `${lastWord}*`;
      return words.join(' ');
    }

    openSearchResults(instance) {
      const state = this.searchStates.get(instance);
      if (!state) return;
      state.results.hidden = false;
      instance.classList.add('is-results-open');
      this.classList.add('silvra-header--search-results-open');
      this.sectionHost?.classList.add('silvra-header-section--search-open');
      state.input.setAttribute('aria-expanded', 'true');
    }

    closeSearchResults(instance) {
      const state = this.searchStates.get(instance);
      if (!state) return;
      state.results.hidden = true;
      instance.classList.remove('is-results-open');
      state.input.setAttribute('aria-expanded', 'false');
      state.input.removeAttribute('aria-activedescendant');

      if (!this.querySelector('[data-silvra-search-instance].is-results-open')) {
        this.classList.remove('silvra-header--search-results-open');
        this.sectionHost?.classList.remove('silvra-header-section--search-open');
      }
    }

    handleSearchKeyboard(event, instance) {
      const state = this.searchStates.get(instance);
      if (!state) return false;

      if (event.key === 'Escape' && !state.results.hidden) {
        event.preventDefault();
        this.closeSearchResults(instance);
        state.input.focus();
        return true;
      }

      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || state.results.hidden) return false;
      const options = Array.from(state.results.querySelectorAll('a[href]'));
      if (!options.length) return false;

      event.preventDefault();
      const currentIndex = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        options[currentIndex + 1]?.focus() || options[0].focus();
      } else if (currentIndex <= 0) {
        state.input.focus();
      } else {
        options[currentIndex - 1].focus();
      }
      return true;
    }

    clearOtherSearchInstances(activeInstance) {
      this.querySelectorAll('[data-silvra-search-instance]').forEach((instance) => {
        if (instance !== activeInstance) this.clearSearchInstance(instance);
      });
    }

    clearSearchInstance(instance) {
      const state = this.searchStates.get(instance);
      if (!state) return;
      window.clearTimeout(state.timer);
      state.controller?.abort();
      state.controller = null;
      state.query = '';
      state.results.innerHTML = state.defaultMarkup;
      state.status.textContent = '';
      this.closeSearchResults(instance);
    }

    trapSurfaceFocus(event, surface) {
      const focusable = Array.from(
        surface.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('silvra-header')) {
    customElements.define('silvra-header', SilvraHeader);
  }
})();
